import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import { authService } from '../services/authService';
import { Invoice, Contract, Customer } from '../types';
import InvoiceForm from '../components/InvoiceForm';
import { clearInvoiceCache } from '../hooks/useInvoices';
import { 
  InvoiceDetailsLinesTab,
  InvoiceDetailsBoletosTab,
  InvoiceDetailsSendEmailsTab,
  InvoiceDetailsIbanFirstTab
} from '../components';
import InvoiceDetailsEmailHistoryTab from '../components/InvoiceDetailsEmailHistoryTab';
import InvoiceDetailsStripePaymentsTab from '../components/InvoiceDetailsStripePaymentsTab';
import ModernInvoicePrintButton from '../components/ModernInvoicePrintButton';
import Modal from '../components/Modal';

interface InvoiceDetailsDisplay extends Invoice {
  contract?: Contract;
  customer?: Customer;
}

type InvoiceConfirmActionType = 'post' | 'delete' | 'late-payment';

interface InvoiceConfirmDialogState {
  action: InvoiceConfirmActionType;
  title: string;
  message: string;
  confirmLabel: string;
}

const InvoiceDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState<InvoiceDetailsDisplay | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const initialEdit = new URLSearchParams(location.search).get('mode') === 'edit';
  const [editMode, setEditMode] = useState(initialEdit);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [metaLoading, setMetaLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [isCancellingInvoice, setIsCancellingInvoice] = useState(false);
  const [cancelContext, setCancelContext] = useState<{ action: 'void' | 'cancel'; actionPast: 'voided' | 'cancelled' }>({
    action: 'cancel',
    actionPast: 'cancelled',
  });
  const [actionFeedback, setActionFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<InvoiceConfirmDialogState | null>(null);
  const [confirmDialogLoading, setConfirmDialogLoading] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<'lines' | 'boletos' | 'bank-transfers' | 'emails' | 'email-history' | 'stripe-payments'>('lines');
  const [stripeRefreshKey, setStripeRefreshKey] = useState(0);

  // Skalar upload state
  const [skalarUploading, setSkalarUploading] = useState(false);
  const [skalarUploadStatus, setSkalarUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'failure'>('idle');
  const [skalarUploadError, setSkalarUploadError] = useState<string | null>(null);
  const [skalarDocumentId, setSkalarDocumentId] = useState<string | null>(null);
  const [showSkalarUploadConfirm, setShowSkalarUploadConfirm] = useState(false);
  const [skalarOrganizationId, setSkalarOrganizationId] = useState('');

  /* -------------------------------------------------------------------------- */
  /*                                Data loading                                */
  /* -------------------------------------------------------------------------- */
  useEffect(() => {
    if (id) {
      fetchInvoiceDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch contracts/customers only when entering edit mode.
  useEffect(() => {
    if (!editMode || metaLoading || (contracts.length > 0 && customers.length > 0)) {
      return;
    }

    const loadMeta = async () => {
      try {
        setMetaLoading(true);
        const [contractsResp, customersResp] = await Promise.all([
          apiService.getContracts(1, 100),
          apiService.getCustomers(1, 100)
        ]);
        setContracts(contractsResp.items);
        setCustomers(customersResp.items);
      } catch (err) {
        console.error('Error fetching meta data for InvoiceForm:', err);
      } finally {
        setMetaLoading(false);
      }
    };
    loadMeta();
  }, [editMode, metaLoading, contracts.length, customers.length]);

  const fetchInvoiceDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch invoice details
      const invoiceData = await apiService.getInvoice(id!);

      // Fetch related contract & customer snapshots for display purposes
      const [contractData, customerData] = await Promise.all([
        invoiceData.contract_id
          ? apiService.getContract(invoiceData.contract_id).catch(() => undefined)
          : Promise.resolve(undefined),
        invoiceData.customer_id
          ? apiService.getCustomer(invoiceData.customer_id).catch(() => undefined)
          : Promise.resolve(undefined)
      ]);

      setInvoice({
        ...invoiceData,
        contract: contractData,
        customer: customerData
      });
    } catch (err: any) {
      console.error('Error loading invoice details:', err);
      setError(err.message || 'Failed to load invoice details');
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                                   Helpers                                  */
  /* -------------------------------------------------------------------------- */
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(amount);
  };

  const getStatusBadgeClass = (status: string) => {
    const classes: Record<string, string> = {
      draft: 'badge-secondary',
      sent: 'badge-info',
      paid: 'badge-success',
      overdue: 'badge-danger',
      cancelled: 'badge-warning',
      finalized: 'badge-primary',
      voided: 'badge-dark'
    };
    return classes[status.toLowerCase()] || 'badge-secondary';
  };

  /* -------------------------------------------------------------------------- */
  /*                                 Operations                                 */
  /* -------------------------------------------------------------------------- */
  const handlePrint = () => {
    window.print();
  };

  const handlePostInvoice = () => {
    if (!invoice) return;

    if (invoice.invoice_status !== 'draft') {
      setActionFeedback({ type: 'error', text: 'Only draft invoices can be posted.' });
      return;
    }

    const currentUser = authService.getUser();
    if (!currentUser) {
      setActionFeedback({ type: 'error', text: 'You must be logged in to post an invoice.' });
      return;
    }

    setConfirmDialog({
      action: 'post',
      title: 'Post Invoice',
      message: `Are you sure you want to finalize invoice ${invoice.invoice_number}? This action cannot be undone.`,
      confirmLabel: 'Post Invoice',
    });
  };

  const handleSendLatePayment = () => {
    if (!invoice) return;
    setConfirmDialog({
      action: 'late-payment',
      title: 'Send Late Payment',
      message: `Send late payment notification with updated boleto for ${invoice.invoice_number}?`,
      confirmLabel: 'Send Late Payment',
    });
  };

  const handleCancelInvoice = () => {
    if (!invoice) return;

    const invoiceStatus = String(invoice.invoice_status || '').toLowerCase();
    const paymentStatus = String(invoice.payment_status || '').toLowerCase();

    const isVoiding = invoiceStatus === 'finalized';
    const action = isVoiding ? 'void' : 'cancel';
    const actionPast = isVoiding ? 'voided' : 'cancelled';

    if (invoiceStatus !== 'finalized' && invoiceStatus !== 'draft') {
      setActionFeedback({ type: 'error', text: `Only draft or finalized invoices can be ${actionPast}.` });
      return;
    }

    if (paymentStatus === 'paid') {
      setActionFeedback({ type: 'error', text: `Cannot ${action} a paid invoice.` });
      return;
    }

    setCancelContext({
      action: isVoiding ? 'void' : 'cancel',
      actionPast: isVoiding ? 'voided' : 'cancelled',
    });
    setCancelReason('');
    setCancelError(null);
    setShowCancelModal(true);
  };

  const confirmCancelInvoice = async () => {
    if (!invoice) return;

    if (!cancelReason.trim()) {
      setCancelError(`A reason is required to ${cancelContext.action} this invoice.`);
      return;
    }

    setCancelError(null);
    setIsCancellingInvoice(true);
    setActionFeedback(null);

    try {
      const currentUser = authService.getUser();
      if (!currentUser) {
        setCancelError(`You must be logged in to ${cancelContext.action} an invoice.`);
        return;
      }

      const endpoint = cancelContext.action === 'void'
        ? '/api/v1/bulk-invoices/void'
        : '/api/v1/bulk-invoices/cancel';
      const response = await apiService.post(endpoint, {
        invoice_ids: [invoice.invoice_id],
        reason: cancelReason.trim()
      }) as {
        successful?: Array<{ error?: string; reason?: string }>;
        failed?: Array<{ error?: string }>;
        skipped?: Array<{ reason?: string }>;
        errors?: string[];
      };

      const successful = response.successful || [];
      const failed = response.failed || [];
      const skipped = response.skipped || [];
      const errors = response.errors || [];

      if (successful.length > 0) {
        setActionFeedback({
          type: 'success',
          text: `Invoice ${invoice.invoice_number} has been ${cancelContext.actionPast} successfully.`,
        });
        setShowCancelModal(false);
        clearInvoiceCache();
        fetchInvoiceDetails();
      } else if (failed.length > 0) {
        setCancelError(`Failed to ${cancelContext.action} invoice: ${failed[0].error || 'Unknown error'}`);
      } else if (skipped.length > 0) {
        setCancelError(`Cannot ${cancelContext.action} invoice: ${skipped[0].reason || 'Operation skipped'}`);
      } else if (errors.length > 0) {
        setCancelError(`Failed to ${cancelContext.action} invoice: ${errors[0]}`);
      } else {
        setCancelError(`Failed to ${cancelContext.action} invoice: no result was returned by the server.`);
      }
    } catch (error: any) {
      console.error(`Error ${cancelContext.action}ing invoice:`, error);
      const errorMessage = error.response?.data?.detail || error.message || `Failed to ${cancelContext.action} invoice.`;
      setCancelError(errorMessage);
    } finally {
      setIsCancellingInvoice(false);
    }
  };

  const handleDeleteInvoice = () => {
    if (!invoice) return;
    setConfirmDialog({
      action: 'delete',
      title: 'Delete Invoice',
      message: `Are you sure you want to delete invoice ${invoice.invoice_number}?`,
      confirmLabel: 'Delete Invoice',
    });
  };

  const handleSubmitEditInvoice = async (data: any) => {
    try {
      const currentUser = authService.getUser();
      if (!currentUser) {
        setActionFeedback({ type: 'error', text: 'You must be logged in to update an invoice.' });
        return;
      }
      if (!invoice) return;

      await apiService.updateInvoice(invoice.invoice_id, {
        ...data,
        invoice_updatedby: currentUser.username || currentUser.email
      });

      setActionFeedback({ type: 'success', text: 'Invoice updated successfully.' });
      setEditMode(false);
      clearInvoiceCache();
      fetchInvoiceDetails();
    } catch (error: any) {
      console.error('Error updating invoice:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to update invoice.';
      setActionFeedback({ type: 'error', text: `Failed to update invoice: ${errorMessage}` });
    }
  };

  const executeConfirmDialog = async () => {
    if (!invoice || !confirmDialog) {
      return;
    }

    setConfirmDialogLoading(true);
    try {
      if (confirmDialog.action === 'post') {
        const currentUser = authService.getUser();
        if (!currentUser) {
          setActionFeedback({ type: 'error', text: 'You must be logged in to post an invoice.' });
          return;
        }

        await apiService.updateInvoiceStatus(invoice.invoice_id, {
          invoice_status: 'finalized',
          invoice_updatedby: currentUser.username || currentUser.email
        });

        clearInvoiceCache();
        fetchInvoiceDetails();
        setActionFeedback({ type: 'success', text: 'Invoice posted successfully.' });
      } else if (confirmDialog.action === 'delete') {
        await apiService.deleteInvoice(invoice.invoice_id);
        clearInvoiceCache();
        navigate('/invoices', {
          replace: true,
          state: { flashFeedback: { type: 'success', text: 'Invoice deleted successfully.' } },
        });
        return;
      } else if (confirmDialog.action === 'late-payment') {
        const res = await apiService.boleto.sendUpdatedBoleto(invoice.invoice_id);
        if (!(res as any)?.success) {
          throw new Error((res as any)?.message || 'Failed to send late payment email');
        }
        fetchInvoiceDetails();
        setActionFeedback({ type: 'success', text: 'Late payment email sent successfully.' });
      }

      setConfirmDialog(null);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Action failed.';
      setActionFeedback({ type: 'error', text: errorMessage });
    } finally {
      setConfirmDialogLoading(false);
    }
  };

  const handleCreatePaymentIntent = async () => {
    try {
      if (!invoice) return;
      const response = await apiService.createStripePaymentIntent(invoice.invoice_id);
      
      // Only show success message if we have a payment_intent_id and preferably a transaction
      if (response?.payment_intent_id) {
        setActionFeedback({ 
          type: 'success', 
          text: response?.transaction_id 
            ? 'Payment intent created successfully!' 
            : 'Payment intent created (waiting for transaction confirmation)' 
        });
      } else {
        setActionFeedback({ 
          type: 'error', 
          text: 'Payment intent creation failed - no payment intent ID received' 
        });
      }
      fetchInvoiceDetails();
      // Increment refresh key to force Stripe payments tab to refresh its transaction data
      setStripeRefreshKey(prev => prev + 1);
    } catch (error: any) {
      console.error('Error creating payment intent:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create payment intent.';
      setActionFeedback({ type: 'error', text: errorMessage });
    }
  };

  const handleShowPaymentTabs = () => {
    setActionFeedback({ type: 'error', text: 'Invoice must be finalized to access payment options.' });
  };

  /* -------------------------------------------------------------------------- */
  /*                            Skalar Upload Functions                          */
  /* -------------------------------------------------------------------------- */

  const handleUploadToSkalar = () => {
    if (!invoice) return;
    
    // Set organization ID from invoice if available
    const orgId = invoice.skalar_organization_id || '';
    setSkalarOrganizationId(orgId);
    setShowSkalarUploadConfirm(true);
  };

  const executeUploadToSkalar = async () => {
    if (!invoice) return;
    
    setSkalarUploading(true);
    setSkalarUploadStatus('uploading');
    setSkalarUploadError(null);
    setSkalarDocumentId(null);
    setShowSkalarUploadConfirm(false);

    try {
      const response = await apiService.post(
        `/api/v1/invoices/${invoice.invoice_id}/upload-to-skalar`,
        {
          organization_id: skalarOrganizationId || undefined
        }
      ) as {
        success: boolean;
        message: string;
        invoice_id: string;
        skalar_document_id: string;
        skalar_upload_status: string;
        upload_date: string;
        error: string;
      };

      if (response.success) {
        setSkalarUploadStatus('success');
        setSkalarDocumentId(response.skalar_document_id || null);
        setActionFeedback({
          type: 'success',
          text: response.message || 'Invoice uploaded to Skalar successfully'
        });
        
        // Refresh invoice details to get updated Skalar status
        fetchInvoiceDetails();
      } else {
        setSkalarUploadStatus('failure');
        setSkalarUploadError(response.error || response.message || 'Failed to upload to Skalar');
        setActionFeedback({
          type: 'error',
          text: response.error || response.message || 'Failed to upload to Skalar'
        });
      }
    } catch (error: any) {
      console.error('Error uploading invoice to Skalar:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to upload to Skalar';
      setSkalarUploadStatus('failure');
      setSkalarUploadError(errorMessage);
      setActionFeedback({ type: 'error', text: errorMessage });
    } finally {
      setSkalarUploading(false);
    }
  };

  const handleCloseSkalarModal = () => {
    setShowSkalarUploadConfirm(false);
    setSkalarOrganizationId('');
  };

  /* -------------------------------------------------------------------------- */
  /*                                   Render                                   */
  /* -------------------------------------------------------------------------- */
  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>{error || 'Invoice not found'}</p>
        <button className="btn btn-primary" onClick={() => navigate('/invoices')}>
          Back to Invoices
        </button>
      </div>
    );
  }

  const invoiceStatus = String(invoice.invoice_status || '').toLowerCase();
  const paymentStatus = String(invoice.payment_status || '').toLowerCase();

  /* ------------------------------ Edit Mode UI ------------------------------ */
  if (editMode) {
    return (
      <div>
        <div className="page-header">
          <div>
            <h1>Edit Invoice #{invoice.invoice_number}</h1>
          </div>
          <div>
            <button
              className="btn btn-secondary"
              onClick={() => setEditMode(false)}
              style={{ marginRight: '10px' }}
            >
              Cancel
            </button>
          </div>
        </div>

        <InvoiceForm
          contracts={contracts}
          customers={customers}
          initialData={invoice as Invoice}
          onSubmit={handleSubmitEditInvoice}
          onCancel={() => setEditMode(false)}
        />
      </div>
    );
  }

  /* ----------------------------- View Mode (read) ---------------------------- */
  return (
    <div>
      <div className="page-header no-print">
        <div>
          <h1>Invoice #{invoice.invoice_number}</h1>
          <span className={`badge ${getStatusBadgeClass(invoice.invoice_status)}`}>{invoice.invoice_status}</span>
        </div>
        <div>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/invoices')}
            style={{ marginRight: '10px' }}
          >
            Back to List
          </button>
          {invoiceStatus === 'draft' && (
            <>
              <button
                className="btn btn-primary"
                onClick={() => setEditMode(true)}
                style={{ marginRight: '10px' }}
              >
                Edit Invoice
              </button>
              <button
                className="btn btn-success"
                onClick={handlePostInvoice}
                style={{ marginRight: '10px' }}
                title="Finalize Invoice"
              >
                Post Invoice
              </button>
            </>
          )}
          {invoiceStatus === 'finalized' && paymentStatus !== 'paid' && (
            <button
              className="btn btn-warning"
              onClick={handleCancelInvoice}
              style={{ marginRight: '10px' }}
              title="Void Invoice"
              disabled={isCancellingInvoice}
            >
              {isCancellingInvoice ? 'Voiding...' : 'Void Invoice'}
            </button>
          )}
          {invoice.currency === 'BRL' && (invoice.payment_status as string) !== 'paid' && new Date(invoice.due_date) < new Date() && (
            <button
              className="btn btn-outline-danger"
              onClick={handleSendLatePayment}
              style={{ marginRight: '10px' }}
              title="Send late payment"
            >
              Send Late Payment
            </button>
          )}
          {invoiceStatus === 'draft' && (
            <button
              className="btn btn-warning"
              onClick={handleCancelInvoice}
              style={{ marginRight: '10px' }}
              title="Cancel Invoice"
              disabled={isCancellingInvoice}
            >
              {isCancellingInvoice ? 'Cancelling...' : 'Cancel Invoice'}
            </button>
          )}
          <button className="btn btn-danger" onClick={handleDeleteInvoice} title="Delete Invoice">
            Delete Invoice
          </button>
          <button 
            className="btn btn-info" 
            onClick={handlePrint} 
            style={{ marginLeft: '10px' }}
            title="Print Invoice (Browser)"
          >
            Print (Browser)
          </button>
                    <ModernInvoicePrintButton
            invoiceId={invoice.invoice_id}
            variant="open"
            className="btn-success"
            style={{ marginLeft: '10px' }}
            title="View Modern Invoice PDF"
          >
            🎨 Modern PDF
          </ModernInvoicePrintButton>
          <button
            className="btn btn-info"
            style={{ marginLeft: '10px' }}
            onClick={() => navigate(`/invoices/${invoice.invoice_id}/modern`)}
            title="Full Modern Invoice Preview"
          >
          🖼️ Modern Preview
          </button>
          <button
            className="btn btn-outline-primary"
            style={{ marginLeft: '10px' }}
            onClick={() => navigate(`/customers/${invoice.customer_id}`)}
            disabled={!invoice.customer_id}
            title="Open Customer Details"
          >
            Open Customer
          </button>
          <button
            className="btn btn-outline-primary"
            style={{ marginLeft: '10px' }}
            onClick={() => navigate(`/contracts/${invoice.contract_id}`)}
            disabled={!invoice.contract_id}
            title="Open Contract Details"
          >
            Open Contract
          </button>
          {invoice.invoice_status === 'finalized' && invoice.payment_status !== 'paid' && (
            <button
              className="btn btn-success"
              style={{ marginLeft: '10px' }}
              onClick={handleCreatePaymentIntent}
              title="Create Stripe Payment Intent"
            >
              💳 Create Payment Intent
            </button>
          )}
          {invoice.invoice_status === 'finalized' && (
            <button
              className="btn btn-primary"
              style={{ marginLeft: '10px' }}
              onClick={handleUploadToSkalar}
              title="Upload Invoice to Skalar Accounting"
              disabled={skalarUploading}
            >
              📤 Upload to Skalar
            </button>
          )}
          </div>
      </div>

      {actionFeedback && (
        <div
          className={`no-print ${actionFeedback.type === 'success' ? 'alert alert-success' : 'alert alert-danger'}`}
          style={{ marginBottom: '12px' }}
        >
          {actionFeedback.text}
        </div>
      )}

      {/* Printable Invoice Content */}
      <div ref={printRef} className="invoice-print-container">
        {/* Print Header - Only visible when printing */}
        <div className="print-header">
          <div className="print-company-info">
            <h2>Invoice</h2>
            <p><strong>Invoice Number:</strong> {invoice.invoice_number}</p>
            <p><strong>Invoice Date:</strong> {new Date(invoice.invoice_date).toLocaleDateString()}</p>
            <p><strong>Due Date:</strong> {new Date(invoice.due_date).toLocaleDateString()}</p>
          </div>
          <div className="print-status">
            <p><strong>Status:</strong> {invoice.invoice_status}</p>
            <p><strong>Payment Status:</strong> {invoice.payment_status}</p>
          </div>
        </div>

      {/* ------------------------------ Top Section ------------------------------ */}
      <div className="invoice-details-grid">
        {/* Invoice Info */}
        <div className="card">
          <div className="card-header">
            <h3>Invoice Information</h3>
          </div>
          <div className="card-body">
            <DetailRow label="Invoice Number" value={invoice.invoice_number} />
            <DetailRow label="Invoice Name" value={invoice.invoice_name} />
            <DetailRow label="Invoice Date" value={new Date(invoice.invoice_date).toLocaleDateString()} />
            <DetailRow label="Due Date" value={new Date(invoice.due_date).toLocaleDateString()} />
            <DetailRow
              label="Billing Period"
              value={`${new Date(invoice.billing_period_start).toLocaleDateString()} - ${new Date(
                invoice.billing_period_end
              ).toLocaleDateString()}`}
            />
            <DetailRow label="Invoice Status" value={invoice.invoice_status} />
            <DetailRow label="Sending Status" value={invoice.sending_status} />
            <DetailRow label="Payment Status" value={invoice.payment_status} />
            <DetailRow 
              label="Skalar Upload Status" 
              value={invoice.skalar_upload_status || 'not_uploaded'}
            />
            {invoice.skalar_document_id && (
              <DetailRow label="Skalar Document ID" value={invoice.skalar_document_id} />
            )}
            {invoice.skalar_upload_error && (
              <DetailRow label="Skalar Upload Error" value={invoice.skalar_upload_error} />
            )}
            {invoice.sent_date && (
              <DetailRow label="Sent Date" value={new Date(invoice.sent_date).toLocaleDateString()} />
            )}
            {invoice.payment_date && (
              <DetailRow label="Payment Date" value={new Date(invoice.payment_date).toLocaleDateString()} />
            )}
          </div>
        </div>

        {/* Customer Info */}
        <div className="card">
          <div className="card-header">
            <h3>Customer Information</h3>
          </div>
          <div className="card-body">
            <DetailRow
              label="Customer"
              value={
                <>
                  {invoice.customer_name || invoice.customer?.customer_name || `Customer ID: ${invoice.customer_id}`}
                  {invoice.customer_id && (
                    <button
                      className="btn btn-link btn-sm"
                      style={{ marginLeft: '8px', padding: 0 }}
                      onClick={() => navigate(`/customers/${invoice.customer_id}`)}
                      title="Open Customer"
                    >
                      ↗
                    </button>
                  )}
                </>
              }
            />
            <DetailRow
              label="Contract"
              value={
                <>
                  {invoice.contract_name || invoice.contract?.contract_name || `Contract ID: ${invoice.contract_id}`}
                  {invoice.contract_id && (
                    <button
                      className="btn btn-link btn-sm"
                      style={{ marginLeft: '8px', padding: 0 }}
                      onClick={() => navigate(`/contracts/${invoice.contract_id}`)}
                      title="Open Contract"
                    >
                      ↗
                    </button>
                  )}
                </>
              }
            />
            <DetailRow label="Email" value={invoice.customer_email_address || invoice.customer?.customer_email_address || '-'} />
            <DetailRow label="Phone" value={invoice.customer_telephone_number || invoice.customer?.customer_telephone_number || '-'} />
            {invoice.customer_address_street && (
              <DetailRow 
                label="Address" 
                value={`${invoice.customer_address_street}, ${invoice.customer_address_postcode} ${invoice.customer_address_city}, ${invoice.customer_address_country}`} 
              />
            )}
            {invoice.customer_vat_number && (
              <DetailRow label="VAT Number" value={invoice.customer_vat_number} />
            )}
          </div>
        </div>

        {/* Financial Summary */}
        <div className="card">
          <div className="card-header">
            <h3>Financial Summary</h3>
          </div>
          <div className="card-body">
            <DetailRow label="Subtotal" value={formatCurrency(invoice.subtotal, invoice.currency)} />
            <DetailRow
              label={`Tax (${invoice.tax_rate}%)`}
              value={formatCurrency(invoice.tax_amount, invoice.currency)}
            />
            <DetailRow
              label="Total Amount"
              value={formatCurrency(invoice.total_amount, invoice.currency)}
              isTotal
            />
          </div>
        </div>

        {/* Notes */}
        {invoice.notes && (
          <div className="card">
            <div className="card-header">
              <h3>Notes</h3>
            </div>
            <div className="card-body">
              <p className="read-only">{invoice.notes}</p>
            </div>
          </div>
        )}
      </div>

      {/* --------------------------- Tabbed Interface -------------------------- */}
      <div className="tabs-container" style={{ marginTop: '20px' }}>
        {/* Tab Navigation */}
        <div className="tab-nav">
          <button
            className={`tab-button ${activeTab === 'lines' ? 'active' : ''}`}
            onClick={() => setActiveTab('lines')}
          >
            Invoice Lines
          </button>
          {invoice.invoice_status === 'finalized' ? (
            <>
              <button
                className={`tab-button ${activeTab === 'boletos' ? 'active' : ''}`}
                onClick={() => setActiveTab('boletos')}
              >
                Boletos
              </button>
              <button
                className={`tab-button ${activeTab === 'bank-transfers' ? 'active' : ''}`}
                onClick={() => setActiveTab('bank-transfers')}
              >
                Bank Transfers
              </button>
              <button
                className={`tab-button ${activeTab === 'stripe-payments' ? 'active' : ''}`}
                onClick={() => setActiveTab('stripe-payments')}
              >
                Stripe Payments
              </button>
            </>
          ) : (
            <button
              className="tab-button"
              onClick={handleShowPaymentTabs}
              title="Show payment options"
            >
              💳 Show Payment Options
            </button>
          )}
          <button
            className={`tab-button ${activeTab === 'emails' ? 'active' : ''}`}
            onClick={() => setActiveTab('emails')}
          >
            Send Emails
          </button>
          <button
            className={`tab-button ${activeTab === 'email-history' ? 'active' : ''}`}
            onClick={() => setActiveTab('email-history')}
          >
            Email History
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'lines' && (
            <InvoiceDetailsLinesTab invoice={invoice} onRefreshInvoice={fetchInvoiceDetails} />
          )}
          {activeTab === 'boletos' && (
            <InvoiceDetailsBoletosTab 
              invoice={invoice} 
              onRefreshInvoice={fetchInvoiceDetails}
            />
          )}
          {activeTab === 'emails' && (
            <InvoiceDetailsSendEmailsTab 
              invoice={invoice} 
              onRefreshInvoice={fetchInvoiceDetails}
            />
          )}
          {activeTab === 'bank-transfers' && (
            <InvoiceDetailsIbanFirstTab
              invoice={invoice}
              onRefreshInvoice={fetchInvoiceDetails}
            />
          )}
          {activeTab === 'email-history' && (
            <InvoiceDetailsEmailHistoryTab 
              invoice={invoice}
            />
          )}
          {activeTab === 'stripe-payments' && (
            <InvoiceDetailsStripePaymentsTab 
              invoice={invoice} 
              onRefreshInvoice={fetchInvoiceDetails}
              refreshKey={stripeRefreshKey}
            />
          )}
        </div>
      </div>
      </div> {/* End of printable content */}

      <Modal
        title={`${cancelContext.action === 'void' ? 'Void' : 'Cancel'} Invoice`}
        isOpen={showCancelModal}
        onClose={() => {
          if (!isCancellingInvoice) {
            setShowCancelModal(false);
          }
        }}
        size="md"
      >
        <p style={{ marginBottom: '12px' }}>
          Provide a reason to {cancelContext.action} invoice <strong>{invoice.invoice_number}</strong>.
        </p>
        <textarea
          className="form-control"
          rows={4}
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          placeholder={`Reason for ${cancelContext.action}ing this invoice`}
          disabled={isCancellingInvoice}
        />
        {cancelError && (
          <div className="alert alert-danger" style={{ marginTop: '12px', marginBottom: 0 }}>
            {cancelError}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowCancelModal(false)}
            disabled={isCancellingInvoice}
          >
            Close
          </button>
          <button
            type="button"
            className="btn btn-warning"
            onClick={confirmCancelInvoice}
            disabled={isCancellingInvoice}
          >
            {isCancellingInvoice
              ? (cancelContext.action === 'void' ? 'Voiding...' : 'Cancelling...')
              : (cancelContext.action === 'void' ? 'Void Invoice' : 'Cancel Invoice')}
          </button>
        </div>
      </Modal>

      {confirmDialog && (
        <Modal
          title={confirmDialog.title}
          isOpen={true}
          onClose={() => {
            if (!confirmDialogLoading) {
              setConfirmDialog(null);
            }
          }}
          size="md"
        >
          <p>{confirmDialog.message}</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setConfirmDialog(null)}
              disabled={confirmDialogLoading}
            >
              Close
            </button>
            <button
              type="button"
              className={confirmDialog.action === 'delete' ? 'btn btn-danger' : 'btn btn-primary'}
              onClick={executeConfirmDialog}
              disabled={confirmDialogLoading}
            >
              {confirmDialogLoading ? 'Processing...' : confirmDialog.confirmLabel}
            </button>
          </div>
        </Modal>
      )}

      {/* Skalar Upload Confirmation Modal */}
      {showSkalarUploadConfirm && invoice && (
        <Modal
          title="Upload Invoice to Skalar"
          isOpen={showSkalarUploadConfirm}
          onClose={handleCloseSkalarModal}
          size="md"
        >
          <p style={{ marginBottom: '12px' }}>
            Upload invoice <strong>{invoice.invoice_number}</strong> to Skalar accounting system?
          </p>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
              Organization ID (optional):
            </label>
            <input
              type="text"
              className="form-control"
              value={skalarOrganizationId}
              onChange={(e) => setSkalarOrganizationId(e.target.value)}
              placeholder={invoice.skalar_organization_id || 'Leave blank to use default'}
            />
            <small style={{ color: '#6c757d', display: 'block', marginTop: '4px' }}>
              Leave blank to use the configured default organization
            </small>
          </div>
          {skalarUploadError && (
            <div className="alert alert-danger" style={{ marginTop: '12px', marginBottom: 0 }}>
              {skalarUploadError}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCloseSkalarModal}
              disabled={skalarUploading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={executeUploadToSkalar}
              disabled={skalarUploading}
            >
              {skalarUploading ? 'Uploading...' : 'Upload to Skalar'}
            </button>
          </div>
        </Modal>
      )}

      <style>{`
        .read-only {
          color: #6c757d;
        }

        /* Tab Styles */
        .tabs-container {
          margin-top: 20px;
        }

        .tab-nav {
          display: flex;
          border-bottom: 2px solid #e9ecef;
          margin-bottom: 0;
          background-color: #f8f9fa;
          border-radius: 8px 8px 0 0;
        }

        .tab-button {
          background: none;
          border: none;
          padding: 12px 24px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: #6c757d;
          border-bottom: 3px solid transparent;
          transition: all 0.2s ease;
          position: relative;
        }

        .tab-button:hover {
          color: #007bff;
          background-color: #e9ecef;
        }

        .tab-button.active {
          color: #007bff;
          border-bottom-color: #007bff;
          background-color: white;
        }

        .tab-button:first-child {
          border-radius: 8px 0 0 0;
        }

        .tab-button:last-child {
          border-radius: 0 8px 0 0;
        }

        .tab-content {
          background-color: white;
          border-radius: 0 0 8px 8px;
          border: 1px solid #e9ecef;
          border-top: none;
          min-height: 400px;
        }

        .tab-content > * {
          border-radius: 0 0 8px 8px;
        }

        .tab-content .card {
          border: none;
          border-radius: 0 0 8px 8px;
        }

        .invoice-details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 20px;
          margin-top: 20px;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid #eee;
        }
        .detail-row:last-child {
          border-bottom: none;
        }
        .detail-label {
          font-weight: 600;
          color: #666;
        }
        .detail-value {
          color: #333;
        }
        .detail-row.total .detail-label,
        .detail-row.total .detail-value {
          font-weight: 700;
        }

        /* Print-specific styles */
        .print-header {
          display: none;
        }

        @media print {
          /* Hide non-printable elements */
          .no-print {
            display: none !important;
          }

          /* Show print-only elements */
          .print-header {
            display: flex;
            justify-content: space-between;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 2px solid #333;
          }

          .print-company-info h2 {
            margin: 0 0 10px 0;
            font-size: 28px;
            color: #333;
          }

          .print-company-info p,
          .print-status p {
            margin: 5px 0;
            font-size: 12px;
          }

          /* Reset page margins for printing */
          @page {
            margin: 0.5in;
            size: A4;
          }

          /* Ensure content fits on page */
          body {
            margin: 0;
            padding: 0;
          }

          /* Style adjustments for print */
          .invoice-print-container {
            width: 100%;
            max-width: none;
            margin: 0;
            padding: 0;
          }

          /* Card styling for print */
          .card {
            border: 1px solid #ddd;
            box-shadow: none;
            page-break-inside: avoid;
            margin-bottom: 20px;
          }

          .card-header {
            background-color: #f8f9fa;
            border-bottom: 1px solid #ddd;
            padding: 10px 15px;
          }

          .card-header h3 {
            margin: 0;
            font-size: 16px;
            color: #333;
          }

          .card-body {
            padding: 15px;
          }

          /* Table styling for print */
          .table-container {
            overflow: visible;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 12px;
          }

          table th {
            background-color: #f8f9fa;
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
            font-weight: bold;
          }

          table td {
            border: 1px solid #ddd;
            padding: 8px;
          }

          /* Ensure proper page breaks */
          .invoice-details-grid {
            page-break-inside: avoid;
          }

          /* Color adjustments for print */
          .read-only {
            color: #333 !important;
          }

          .detail-label {
            color: #333 !important;
          }

          .detail-value {
            color: #000 !important;
          }

          /* Hide badges in print */
          .badge {
            display: none;
          }

          /* Ensure text is black for printing */
          * {
            color: #000 !important;
            background: white !important;
          }

          /* Keep headers gray */
          .card-header {
            background-color: #f8f9fa !important;
          }

          table th {
            background-color: #f8f9fa !important;
          }
        }
      `}</style>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                               Helper Component                             */
/* -------------------------------------------------------------------------- */
interface DetailRowProps {
  label: string;
  value: React.ReactNode;
  isTotal?: boolean;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value, isTotal = false }) => (
  <div className={`detail-row ${isTotal ? 'total' : ''}`}>
    <span className="detail-label read-only">{label}:</span>
    <span className="detail-value read-only">{value}</span>
  </div>
);

export default InvoiceDetails;
